import {
  useState,
  useRef,
  useEffect,
  useLayoutEffect,
  useCallback,
} from "react";
import { Check, X, PartyPopper, AlertTriangle, Volume2 } from "lucide-react";
import api from "@/lib/api";
import { isFullyArabic } from "@/lib/script";
import ConfettiBurst from "@/components/ConfettiBurst";
import { t } from "@/i18n";

function speakArabic(text) {
  if (!text || typeof window === "undefined" || !window.speechSynthesis) return;
  window.speechSynthesis.cancel();
  const utter = new SpeechSynthesisUtterance(text);
  utter.lang = "ar-SA";
  utter.rate = 0.85;
  window.speechSynthesis.speak(utter);
}

/*
  ÉCHELLE AUTOMATIQUE
  -------------------
  Toutes les DIMENSIONS (polices, marges, icônes, rayons) sont des
  multiples d'une seule unité `--u`, calculée en JS : on rend, on mesure
  la hauteur réelle du contenu, on ajuste `--u` pour qu'il occupe ~98 %
  de la place disponible. Rien n'est deviné, donc rien ne déborde,
  quel que soit l'écran ou le nombre d'options.

  Les COULEURS restent en classes Tailwind (bg-card, border, text-primary…)
  car les variables de thème du projet sont des canaux HSL : un
  `var(--border)` brut en CSS est invalide et rendait les boutons
  invisibles. Tailwind applique le hsl() correctement.
*/
const U_MIN = 4.5;
const U_MAX = 20;
const FILL = 0.98;
const PASSES = 7;

export default function Quiz({
  questions,
  onDone,
  record = true,
  arabicOptions = false,
  arabicPrompt = false,
  allowSkip = false,
  rewardMode = "classique",
  rewardAmount = 2,
  onHint = null,
  onAnswer = null,
  showCelebration = true,
  title = "",
  onExit = null,
}) {
  const [i, setI] = useState(0);
  const [picked, setPicked] = useState(null);
  const [correct, setCorrect] = useState(0);
  const [results, setResults] = useState([]);
  const [sessionId] = useState(
    () => `${Date.now()}-${Math.random().toString(36).slice(2)}`,
  );
  const [celebrating, setCelebrating] = useState(false);
  const [earned, setEarned] = useState(0);
  const [confirmExit, setConfirmExit] = useState(false);

  const cardRef = useRef(null);
  const innerRef = useRef(null);
  const lastU = useRef(10);

  const q = questions?.[i];
  const promptIsArabic = q?.arabicPrompt ?? arabicPrompt;
  const optionsAreArabic = q?.arabicOptions ?? arabicOptions;

  // Empêche la page (et donc la navbar) de défiler derrière le calque fixe.
  useEffect(() => {
    const body = document.body;
    const prevOverflow = body.style.overflow;
    const prevOverscroll = body.style.overscrollBehavior;
    body.style.overflow = "hidden";
    body.style.overscrollBehavior = "none";
    return () => {
      body.style.overflow = prevOverflow;
      body.style.overscrollBehavior = prevOverscroll;
    };
  }, []);

  const fit = useCallback(() => {
    const card = cardRef.current;
    const inner = innerRef.current;
    if (!card || !inner) return;

    const avail = card.clientHeight;
    const width = card.clientWidth;
    if (!avail || !width) return;

    // Sur un écran large et court, la hauteur seule autoriserait une
    // échelle énorme : on borne aussi par la largeur.
    const uMax = Math.min(U_MAX, width / 24);
    const target = avail * FILL;

    let u = Math.min(Math.max(lastU.current, U_MIN), uMax);

    for (let pass = 0; pass < PASSES; pass++) {
      inner.style.setProperty("--u", `${u}px`);
      const measured = inner.scrollHeight; // force le reflow
      if (!measured) break;

      const ratio = target / measured;
      if (ratio > 0.99 && ratio < 1.01) break;

      const nextU = Math.min(Math.max(u * ratio, U_MIN), uMax);
      if (Math.abs(nextU - u) < 0.05) {
        u = nextU;
        break;
      }
      u = nextU;
    }

    inner.style.setProperty("--u", `${u}px`);
    lastU.current = u;
  }, []);

  useLayoutEffect(() => {
    fit();
  }, [fit, i, picked, celebrating, questions?.length]);

  useEffect(() => {
    const card = cardRef.current;
    if (!card) return;
    const ro = new ResizeObserver(() => fit());
    ro.observe(card);
    window.addEventListener("resize", fit);
    window.addEventListener("orientationchange", fit);
    if (document.fonts?.ready) document.fonts.ready.then(fit).catch(() => {});
    return () => {
      ro.disconnect();
      window.removeEventListener("resize", fit);
      window.removeEventListener("orientationchange", fit);
    };
  }, [fit]);

  const buildResult = (idx, good) => ({
    key: q.key || (q.char ? `alpha:${q.char}` : null),
    char: q.char ?? null,
    correct: good,
    skill: q.skill || "grammar",
    ref: q.ref || null,
    prompt: q.char ? t("quiz.lettre_prompt", { char: q.char }) : q.prompt,
    options: q.options,
    answer: q.answer,
    reward_mode: q.reward_mode || rewardMode,
    reward_event_key: `quiz:${sessionId}:${i}:${q.key || q.char || q.prompt}`,
  });

  /* Chaque réponse est enregistrée sur-le-champ, dès qu'elle est donnée —
     plus de lot envoyé seulement à `finish()`. Avant ce changement,
     quitter avant la fin d'un bloc (bouton Quitter, ou tout simplement
     une session en mode infini qui ne se termine jamais) perdait
     silencieusement toutes les réponses déjà données : rien n'avait
     encore été posté au backend. Chaque item garde un
     `reward_event_key` unique, donc un double-envoi éventuel ne peut
     pas doubler la récompense côté serveur. */
  const recordAnswer = (r) => {
    if (!record) return;
    api
      .post("/progress/answers", { items: [r] })
      .then(({ data }) => {
        window.dispatchEvent(
          new CustomEvent("arabdex:dinar-update", { detail: data }),
        );
      })
      .catch(() => console.error("Échec de l'enregistrement"));
  };

  const choose = (idx) => {
    if (picked !== null) return;
    setPicked(idx);
    const good = idx === q.answer;
    if (good) setCorrect((c) => c + 1);
    const r = buildResult(idx, good);
    setResults((rs) => [...rs, r]);
    recordAnswer(r);
    if (onAnswer) onAnswer(q, good);
  };

  const finish = (rs, corr) => {
    const total = corr * rewardAmount;
    setEarned(total);
    if (showCelebration) {
      setCelebrating(true);
    } else {
      onDone &&
        onDone({
          correct: corr,
          total: questions.length,
          results: rs,
          earned: total,
        });
    }
  };

  const closeCelebration = () => {
    setCelebrating(false);
    onDone && onDone({ correct, total: questions.length, results, earned });
  };

  const next = () => {
    if (i + 1 >= questions.length) finish(results, correct);
    else {
      setI(i + 1);
      setPicked(null);
    }
  };

  // ---------------------------------------------------------------
  // Contenu
  // ---------------------------------------------------------------
  let content;

  if (questions && questions.length === 0) {
    content = (
      <div className="q-message text-muted-foreground">
        {t("quiz.aucune_question_disponible_pour_cet_exercice")}
      </div>
    );
  } else if (celebrating) {
    content = (
      <div className="q-celebrate">
        {earned > 0 && <ConfettiBurst />}
        <PartyPopper className="q-party text-amber-500" />
        <h2 className="q-celebrate-title font-head">
          {t("quiz.exercice_termine")}
        </h2>
        <p className="q-celebrate-sub text-muted-foreground">
          {correct} / {questions.length}{" "}
          {t("interexerciserecap.bonnes_reponses")}
        </p>
        <div className="q-celebrate-amount font-head text-amber-500">
          +{earned} 🪙
        </div>
        <p className="q-celebrate-sub text-muted-foreground">
          {earned > 0
            ? t("quiz.gagnes_sur_cet_exercice")
            : t("quiz.aucun_dinar_cette_fois_retente")}
        </p>
        <button
          onClick={closeCelebration}
          className="q-cta bg-primary text-primary-foreground shadow-md hover:opacity-90"
        >
          {t("exerciseblock.continuer")}
        </button>
      </div>
    );
  } else if (!q) {
    content = (
      <div className="q-message text-muted-foreground">
        {t("quiz.chargement_des_questions")}
      </div>
    );
  } else {
    content = (
      <>
        {/* EN-TÊTE */}
        <div className="q-head border-b md:border-b-0">
          <div className="q-head-row">
            {title ? <h1 className="q-title font-head">{title}</h1> : <span />}
            {onExit && (
              <button
                onClick={() => setConfirmExit(true)}
                aria-label={t("alphabetlearnscreen.quitter")}
                className="q-icon-btn border hover:bg-secondary transition"
              >
                <X className="q-ico-sm" />
              </button>
            )}
          </div>
          <div className="q-progress-row text-muted-foreground">
            <span>
              {t("exerciseblock.question")} {i + 1}/{questions.length}
            </span>
            <span style={{ color: "#16A34A" }}>{correct} ✓</span>
          </div>
          <div className="q-bar bg-secondary">
            <div
              className="q-bar-fill bg-primary"
              style={{ width: `${(i / questions.length) * 100}%` }}
            />
          </div>
        </div>

        {/* CORPS */}
        <div className="q-body">
          <div className="q-cols">
            <div className="q-col-subject">
              {q.char && (
                <div className="q-char-block">
                  {(q.eyebrow || q.learning_mode) && (
                    <div className="q-eyebrow bg-primary/10 text-primary">
                      {q.eyebrow ||
                        (q.learning_mode === "sound"
                          ? t("quiz.objectif_son")
                          : t("quiz.objectif_nom"))}
                    </div>
                  )}
                  <div className="arabic q-char" dir="rtl">
                    {q.char}
                  </div>
                  {q.translit && q.learning_mode !== "sound" && (
                    <div className="q-translit text-muted-foreground" dir="ltr">
                      {q.translit} —{" "}
                      {q.letter_name || t("quiz.nom_de_la_lettre")}
                    </div>
                  )}
                </div>
              )}

              <p
                className={
                  promptIsArabic
                    ? t("quiz.arabic_q_prompt_q_prompt_ar")
                    : t("quiz.q_prompt")
                }
                dir={promptIsArabic ? "rtl" : "ltr"}
              >
                {q.prompt}
              </p>

              {promptIsArabic && q.promptLatin && (
                <p className="q-prompt-latin text-muted-foreground" dir="ltr">
                  {q.promptLatin}
                </p>
              )}

              {promptIsArabic && (
                <button
                  type="button"
                  onClick={() => speakArabic(q.prompt)}
                  className="q-icon-btn q-speak-main border text-primary hover:bg-secondary transition"
                >
                  <Volume2 className="q-ico-md" />
                </button>
              )}
            </div>

            <div className="q-col-options">
              {(q.options || []).map((opt, idx) => {
                const isAns = idx === q.answer;
                const show = picked !== null;
                const translation = show && q.optionTranslations?.[idx];
                // Une option mixte (« le mot كَتَبَ ») reste en LTR : seule une
                // option 100 % arabe bascule en RTL et police arabe.
                const optIsAr = optionsAreArabic && isFullyArabic(String(opt));
                const latin = optIsAr && q.optionLatins?.[idx];

                let colors = "border bg-card hover:border-primary";
                if (show && isAns)
                  colors = "border-2 border-emerald-500 bg-emerald-500/10";
                else if (show && idx === picked)
                  colors = "border-2 border-destructive bg-destructive/10";

                return (
                  <div
                    key={idx}
                    role="button"
                    tabIndex={0}
                    onClick={() => choose(idx)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        choose(idx);
                      }
                    }}
                    aria-disabled={show}
                    className={`q-option transition ${colors} ${optIsAr ? "arabic q-option-ar" : ""}`}
                    dir={optIsAr ? "rtl" : "ltr"}
                  >
                    <span className="q-option-text">
                      <span>{opt}</span>
                      {latin && (
                        <span
                          className="q-option-sub text-muted-foreground"
                          dir="ltr"
                        >
                          {latin}
                        </span>
                      )}
                      {translation && (
                        <span
                          className={`q-option-sub text-muted-foreground ${optIsAr ? "" : "arabic"}`}
                          dir={optIsAr ? "ltr" : "rtl"}
                        >
                          {translation}
                        </span>
                      )}
                    </span>

                    <span className="q-option-icons">
                      {optIsAr && opt && (
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            speakArabic(opt);
                          }}
                          className="q-icon-btn border text-primary hover:bg-secondary transition"
                        >
                          <Volume2 className="q-ico-xs" />
                        </button>
                      )}
                      {show && isAns && (
                        <Check className="q-ico-md text-emerald-600" />
                      )}
                      {show && idx === picked && !isAns && (
                        <X className="q-ico-md text-destructive" />
                      )}
                    </span>
                  </div>
                );
              })}

              {picked !== null && q.explain && (
                <p className="q-explain bg-secondary text-muted-foreground">
                  {q.explain}
                </p>
              )}

              {onHint && picked === null && (
                <button
                  type="button"
                  onClick={() => onHint(q)}
                  className="q-hint border border-dashed text-muted-foreground hover:bg-secondary transition"
                >
                  {t("quiz.je_ne_sais_pas_revoir_la")}
                </button>
              )}

              {allowSkip && picked === null && (
                <button
                  type="button"
                  onClick={() => {
                    const r = buildResult(q.answer, true);
                    setResults((rs) => [...rs, r]);
                    recordAnswer(r);
                    setCorrect((c) => c + 1);
                    next();
                  }}
                  className="q-skip border text-muted-foreground hover:bg-secondary transition"
                >
                  {t("quiz.passer_admin")}
                </button>
              )}
            </div>
          </div>
        </div>

        {/* PIED DE PAGE — toujours monté (masqué avant réponse) pour que
            sa place soit réservée : sinon l'apparition du bouton
            changerait la hauteur et referait sauter toute l'échelle. */}
        <div className="q-foot border-t" aria-hidden={picked === null}>
          <div
            className="q-reward text-amber-600 dark:text-amber-400"
            style={{
              visibility:
                picked !== null && picked === q.answer && rewardAmount > 0
                  ? "visible"
                  : "hidden",
            }}
          >
            +{rewardAmount} 🪙
          </div>
          <button
            onClick={next}
            className="q-cta bg-primary text-primary-foreground shadow-md"
            style={{
              visibility: picked !== null ? "visible" : "hidden",
              pointerEvents: picked !== null ? "auto" : "none",
            }}
            tabIndex={picked !== null ? 0 : -1}
          >
            {i + 1 >= questions.length
              ? t("quiz.terminer")
              : t("alphabetlearnscreen.suivant")}
          </button>
        </div>
      </>
    );
  }

  return (
    <>
      <style>{`
        /* Géométrie uniquement — les couleurs viennent de Tailwind. */
        .quiz-container {
          position: fixed;
          top: var(--nav-top-mobile, 4rem);
          bottom: var(--nav-bottom-mobile, 4rem);
          left: 0;
          right: 0;
          z-index: 30;
          display: flex;
          overflow: hidden;
          overscroll-behavior: contain;
        }
        @media (min-width: 768px) {
          .quiz-container {
            top: var(--nav-top-desktop, 5.5rem);
            bottom: 1.25rem;
            padding: 0 clamp(1rem, 2.5vw, 2rem);
            justify-content: center;
          }
        }

        .quiz-card {
          width: 100%;
          height: 100%;
          margin: 0 auto;
          display: flex;
          flex-direction: column;
          justify-content: center;
          overflow: hidden;
          container-type: inline-size;
          container-name: qcard;
        }
        @media (min-width: 768px) {
          .quiz-card { max-width: 1000px; border-radius: 1.5rem; }
        }

        /* Hauteur naturelle : c'est ce qu'on mesure pour ajuster --u. */
        .q-inner {
          --u: 10px;
          width: 100%;
          display: flex;
          flex-direction: column;
        }

        .q-message {
          padding: calc(var(--u) * 2.4);
          text-align: center;
          font-size: calc(var(--u) * 1.6);
        }

        .q-head { padding: calc(var(--u) * 1.3) calc(var(--u) * 2.4); }
        .q-head-row {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: calc(var(--u) * 1.2);
          margin-bottom: calc(var(--u) * 0.7);
        }
        .q-title {
          font-size: calc(var(--u) * 1.75);
          font-weight: 700;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }
        .q-progress-row {
          display: flex;
          justify-content: space-between;
          font-size: calc(var(--u) * 1.05);
          font-weight: 500;
          margin-bottom: calc(var(--u) * 0.5);
        }
        .q-bar {
          height: calc(var(--u) * 0.5);
          border-radius: 999px;
          overflow: hidden;
        }
        .q-bar-fill {
          height: 100%;
          border-radius: 999px;
          transition: width 0.3s ease-out;
        }

        .q-body { padding: calc(var(--u) * 1.6) calc(var(--u) * 2.4); }
        .q-cols {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          gap: calc(var(--u) * 2.2);
        }
        @container qcard (min-width: 620px) {
          .q-cols { flex-direction: row; gap: calc(var(--u) * 3.4); }
          .q-col-subject { width: 50%; }
          .q-col-options { width: 50%; }
        }
        /* Repli pour les navigateurs sans container queries (Safari < 16,
            Firefox < 110) : on bascule en deux colonnes sur la largeur de
            la fenêtre à la place. */
        @supports not (container-type: inline-size) {
          @media (min-width: 900px) {
            .q-cols { flex-direction: row; gap: calc(var(--u) * 3.4); }
            .q-col-subject { width: 50%; }
            .q-col-options { width: 50%; }
          }
        }
        .q-col-subject {
          width: 100%;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
        }
        .q-col-options {
          width: 100%;
          display: flex;
          flex-direction: column;
          gap: calc(var(--u) * 0.9);
        }

        .q-char-block {
          text-align: center;
          margin-bottom: calc(var(--u) * 1.6);
        }
        .q-eyebrow {
          display: inline-flex;
          border-radius: 999px;
          font-weight: 700;
          font-size: calc(var(--u) * 1.05);
          padding: calc(var(--u) * 0.35) calc(var(--u) * 1.1);
          margin-bottom: calc(var(--u) * 1.1);
        }
        .q-char {
          font-size: calc(var(--u) * 6.4);
          line-height: 1;
          padding: calc(var(--u) * 0.4) 0;
        }
        .q-translit {
          font-size: calc(var(--u) * 1.1);
          margin-top: calc(var(--u) * 0.7);
        }
        .q-prompt {
          font-size: calc(var(--u) * 2);
          font-weight: 600;
          text-align: center;
          line-height: 1.25;
          margin-bottom: calc(var(--u) * 1.4);
        }
        .q-prompt-ar { font-weight: 500; }
        .q-prompt-latin {
          font-size: calc(var(--u) * 1.15);
          text-align: center;
          margin-bottom: calc(var(--u) * 1.2);
        }
        .q-speak-main { margin: 0 auto; }

        .q-option {
          width: 100%;
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: calc(var(--u) * 1.2);
          text-align: left;
          cursor: pointer;
          border-radius: calc(var(--u) * 1.4);
          padding: calc(var(--u) * 1.1) calc(var(--u) * 1.8);
          font-size: calc(var(--u) * 1.6);
          font-weight: 500;
          line-height: 1.3;
        }
        .q-option-ar { font-size: calc(var(--u) * 1.9); font-weight: 400; }
        .q-option-text { display: flex; flex-direction: column; min-width: 0; }
        .q-option-sub {
          font-size: calc(var(--u) * 1.05);
          font-weight: 400;
        }
        .q-option-icons {
          display: flex;
          align-items: center;
          gap: calc(var(--u) * 0.7);
          flex-shrink: 0;
        }

        .q-explain {
          font-size: calc(var(--u) * 1.1);
          border-radius: calc(var(--u) * 1);
          padding: calc(var(--u) * 1);
          line-height: 1.35;
        }
        .q-hint, .q-skip {
          width: 100%;
          border-radius: calc(var(--u) * 1.2);
          background: transparent;
        }
        .q-hint {
          font-weight: 700;
          font-size: calc(var(--u) * 1.15);
          padding: calc(var(--u) * 0.9);
        }
        .q-skip {
          font-weight: 600;
          font-size: calc(var(--u) * 1);
          padding: calc(var(--u) * 0.7);
        }

        .q-foot { padding: calc(var(--u) * 1.3) calc(var(--u) * 2.4); }
        .q-reward {
          text-align: center;
          font-weight: 700;
          font-size: calc(var(--u) * 1.2);
          margin-bottom: calc(var(--u) * 0.6);
        }

        .q-cta {
          width: 100%;
          border: none;
          border-radius: calc(var(--u) * 1.4);
          font-weight: 700;
          font-size: calc(var(--u) * 1.65);
          padding: calc(var(--u) * 1.2);
          cursor: pointer;
          transition: transform 0.12s;
        }
        .q-cta:hover { transform: scale(1.01); }
        .q-cta:active { transform: scale(0.99); }

        .q-icon-btn {
          flex-shrink: 0;
          width: calc(var(--u) * 3.4);
          height: calc(var(--u) * 3.4);
          border-radius: 999px;
          background: transparent;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
        }
        .q-ico-xs { width: calc(var(--u) * 1.5); height: calc(var(--u) * 1.5); }
        .q-ico-sm { width: calc(var(--u) * 1.7); height: calc(var(--u) * 1.7); }
        .q-ico-md { width: calc(var(--u) * 2); height: calc(var(--u) * 2); }

        .q-celebrate {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          text-align: center;
          position: relative;
          padding: calc(var(--u) * 2.4);
        }
        .q-party {
          width: calc(var(--u) * 5);
          height: calc(var(--u) * 5);
          margin-bottom: calc(var(--u) * 1.6);
        }
        .q-celebrate-title {
          font-size: calc(var(--u) * 3);
          font-weight: 800;
          margin-bottom: calc(var(--u) * 0.6);
        }
        .q-celebrate-sub {
          font-size: calc(var(--u) * 1.5);
          margin-bottom: calc(var(--u) * 1.6);
        }
        .q-celebrate-amount {
          font-size: calc(var(--u) * 5.5);
          font-weight: 800;
          margin-bottom: calc(var(--u) * 1);
        }
        .q-celebrate .q-cta {
          max-width: calc(var(--u) * 26);
          margin-top: calc(var(--u) * 1.4);
        }
      `}</style>

      <div className="quiz-container bg-background md:bg-transparent">
        <div
          className="quiz-card md:bg-card md:border md:shadow-2xl"
          ref={cardRef}
        >
          <div className="q-inner" ref={innerRef}>
            {content}
          </div>
        </div>
      </div>

      {confirmExit && (
        <div
          className="fixed inset-0 z-[100] grid place-items-center bg-black/60 backdrop-blur-sm p-4"
          onClick={() => setConfirmExit(false)}
        >
          <div
            className="w-full max-w-sm md:max-w-md rounded-3xl border bg-card p-6 md:p-8 text-center shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <AlertTriangle className="mx-auto mb-3 md:mb-4 w-10 h-10 md:w-12 md:h-12 text-destructive" />
            <h3 className="font-head text-xl md:text-2xl font-bold mb-2 md:mb-3">
              {t("quiz.quitter_l_exercice")}
            </h3>
            <p className="text-base md:text-lg text-muted-foreground mb-6 md:mb-8">
              {record
                ? t("quiz.tes_reponses_deja_donnees_restent_enregistrees")
                : t("quiz.ta_progression_sur_cette_seance_sera")}
            </p>
            <div className="grid grid-cols-2 gap-3 md:gap-4">
              <button
                onClick={() => setConfirmExit(false)}
                className="py-3 md:py-3.5 rounded-xl border font-bold text-base md:text-lg"
              >
                {t("exerciseblock.continuer")}
              </button>
              <button
                onClick={() => {
                  setConfirmExit(false);
                  onExit();
                }}
                className="py-3 md:py-3.5 rounded-xl bg-destructive text-destructive-foreground font-bold text-base md:text-lg"
              >
                {t("alphabetlearnscreen.quitter")}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
